import * as THREE from "three";
import {CheckOverObstArr, GetClickObj, GetDis, GetVerPosArr} from "./Common";
import {SetDelIconUI, SetupInputObstUI, SetupSwitchObstRot} from "./TransControl";
import {AddStep} from './StepControl';
import {SwitchModuleIrrMat, SetMaxFillForRoofs, addModulesToRoofAutoFill} from "./ModuleControl";
import {useViewerStore} from "../../../../../store/store";
import {SetMeshOBB, UpdateMeshOBB} from "./MeshControl";
import {createPolygonExtrudeGeometry} from "./Loader";
import { Viewer } from "../Viewer-Factory/Viewer";
import { TreeGroup } from "../../../../../@types/types";
import { removeSelectedTree } from "../../../../../helpers/storeHelpers";
import { TreeProperty } from "../../../../../@types/enums";

const delOpacity = 0.3;

export function ToggleSelectedTreeDelete(self: Viewer) {
    const firstGroup = self.selTreeArr[0];
    if (!firstGroup) return;
    const allDelStatusTrue = self.selTreeArr.every(group => group.delStatus === true);
    const newStatus = !allDelStatusTrue;
    const treeIds: string[] = [];
    self.selTreeArr.forEach(group => {
        ChangeTreeDelete(group, newStatus);
        treeIds.push(group.userData.treeId);
    });

    SetDelIconUI('Tree', newStatus);
    self.reCalcShade(treeIds, newStatus);
}

export function ChangeTreeDelete(treeGroup: TreeGroup, value: boolean) {
    treeGroup.delStatus = value;
    treeGroup.children.forEach((child: any) => {
        if (!child.oriColor) return;
        child.material.color.setHex(value ? 0xFF0000 : child.oriColor);
        child.material.map = child.mapSrc;
        child.material.transparent = child.mapSrc ? true : value;
        child.material.opacity = 1;
        child.material.needsUpdate = true;
        child.castShadow = !value;
    });
}

export function ChangeSelectedTreeShape(self: Viewer, value: { cM: string }) {
    const {cM} = value, {selTreeArr, crownMapArr} = self,
        selCrownMap = crownMapArr.find(item => item.crownKey === cM);
    selTreeArr.forEach(group => {
        group.children[0].material = new THREE.MeshStandardMaterial({
            color: 0x90EE90,
            map: selCrownMap,
            transparent: true,
            depthWrite: true,
            side: 1
        });
        group.children[0].userData.crownKey = cM;
    });
    self.refreshShade();
}

export function ChangeTreeShape(self: Viewer, tree: any, crownKey: string) {
    const selCrownMap = self.crownMapArr.find(item => item.crownKey === crownKey);

    tree.children[0].material = new THREE.MeshStandardMaterial({
        color: 0x90EE90,
        map: selCrownMap,
        transparent: true,
        depthWrite: true,
        side: 1
    });
    tree.children[0].userData.crownKey = crownKey;
}

export function ChangeTreePos(self: Viewer, type: string, value: number) {
    const {treeTransMesh, treeGroup, roofMeshArr, cSize} = self, axis = type === 'posX' ? 'x' : 'z';
    // treeTransMesh.position[axis] = value;
    if (treeTransMesh) {
        treeTransMesh.position[axis] = value;
    }
    const { position, oriPos } = (treeTransMesh as THREE.Group & { oriPos: THREE.Vector3 }),
        deltaDis = { x: position.x - oriPos.x, z: position.z - oriPos.z };
    treeGroup.children.forEach((group: any) => {
        if (!group.select) return;
        const newPos = { x: group.oriPos.x + deltaDis.x, z: group.oriPos.z + deltaDis.z };
        group.position.set(newPos.x, 0, newPos.z);
    });
    self.refreshShade();
}

export function SetTreeSize(self: Viewer, treeGroup: any, treeInfo: any) {
    const { height, crown_radius, crown_height, trunk_radius } = treeInfo;
    const trunk_height = height - crown_height, trunkH = trunk_height * 1.3;
    const crownMesh = treeGroup.children[0], trunkMesh = treeGroup.children[1], wrapperMesh = treeGroup.children[2];
    const {crownScl} = crownMesh;
    crownMesh.scale.set(crown_radius * crownScl.x, crown_height * crownScl.y, crown_radius * crownScl.z);


    crownMesh.position.y = trunk_height;
    trunkMesh.scale.set(trunk_radius, trunkH, trunk_radius);
    trunkMesh.position.y = trunkH / 2;
    wrapperMesh.scale.set(crown_radius / 2 * 1, height * 1, crown_radius / 2 * 1);
    wrapperMesh.position.y = height / 2;

    //DEBUGGING =>UNCOMMENT THIS TO SHOW THE TREE CYLINDER
    // wrapperMesh.visible = true;
    // wrapperMesh.material.color.setHex(0xFFFFFF);
    // wrapperMesh.material.color.setHex(0xFFFFFF);

    treeGroup.totalH = height;
    // treeGroup.treeInfo = {...treeInfo};
    treeGroup.userData = { ...treeGroup.userData, height, crown_radius, crown_height, trunk_radius };
}

export function ChangeTreeSize(self: Viewer, type: string, value: number) {
    const { selTreeArr, pushMode, virTree } = self, virFlag = (pushMode && virTree?.visible);
    var selGroup = selTreeArr[0];
    if (virFlag) selGroup = virTree;
    if (!selGroup) return;
    const crownMesh = selGroup.children[0], { crownScl } = crownMesh, trunkMesh = selGroup.children[1];
    if (type === 'height') selGroup.totalH = value;
    const height = type === 'height' ? value : selGroup.totalH;
    const crown_radius = type === 'crown_radius' ? value : crownMesh.scale.x / crownScl.x;
    const crown_height = type === 'crown_height' ? value : crownMesh.scale.y / crownScl.y;
    const trunk_radius = type === 'trunk_radius' ? value : trunkMesh.scale.x;
    if (virFlag) {
        SetTreeSize(self, virTree, { height, crown_radius, crown_height, trunk_radius });
    } else if (!pushMode && selTreeArr.length) {
        selTreeArr.forEach(group => {
            SetTreeSize(self, group, { height, crown_radius, crown_height, trunk_radius });
        });
    }
}

export function RemoveTreeFromCart(self: any) {
    if (!self.selTreeArr || self.selTreeArr.length === 0) return;
    let updatedCartTrees = useViewerStore.getState().treeCartItems;

    const treeIds: string[] = [];
    self.selTreeArr.forEach((treeInCart: any) => {
        treeInCart.userData.treeCartInfo = {};
        treeInCart.select = false;
        removeSelectedTree(treeInCart);
        treeInCart.children[0].material.emissive.setHex(0x000000);
        treeIds.push(treeInCart.userData.treeId);

        //Remove Tree from cart based on selected tree id
        updatedCartTrees = updatedCartTrees.filter((tree) => tree.userData.treeId !== treeInCart.userData.treeId);

        ChangeTreeDelete(treeInCart, false);
    });

    useViewerStore.setState({ treeCartItems: updatedCartTrees });
    self.reCalcShade(treeIds, false);
}

export function GetObstTypeGeo(num: number, sides?: number, nodes?: { X: number; Y: number; Z: number }[], normal_top?: any) {
    if (num === 1) return new THREE.BoxGeometry(1, 1, 1);
    else if (num === 2) return new THREE.CylinderGeometry(0.6, 0.6, 1, sides || 6);
    else if (num === 3) return new THREE.CylinderGeometry(0.6, 0.6, 1, 16);
    else if (num === 4) return createPolygonExtrudeGeometry(nodes, 1, normal_top);
}

export function SetBtnObstActive(shapeNum: number) {
    const btnObstTypeArr = document.getElementsByClassName('btn-obst-type');
    for (let i = 0; i < btnObstTypeArr.length; i++) {
        const element = btnObstTypeArr[i];
        element.classList.remove('active');
    }
    const selBtnObstType = document.getElementsByClassName('btnObstType' + shapeNum);
    if (!selBtnObstType || !selBtnObstType[0]) return;
    selBtnObstType[0].classList.add('active');
    // document.getElementById('obstControlCube').style.display = shapeNum === 1 ? 'block' : 'none';
    const obstControlCube = document.getElementById('obstControlCube');
    if (obstControlCube) {
        obstControlCube.style.display = shapeNum === 1 ? 'block' : 'none';
    }

    // document.getElementById('obstControlPolygon').style.display = shapeNum === 2 ? 'block' : 'none';
    const obstControlPolygon = document.getElementById('obstControlPolygon');
    if (obstControlPolygon) {
        obstControlPolygon.style.display = shapeNum === 2 ? 'block' : 'none';
    }

    // document.getElementById('obstControlCyliner').style.display = shapeNum === 3 ? 'block' : 'none';
    const obstControlCyliner = document.getElementById('obstControlCyliner');
    if (obstControlCyliner) {
        obstControlCyliner.style.display = shapeNum === 3 ? 'block' : 'none';
    }
}

function GetObstSel(self: Viewer) {
    const { obstGroup, obstId, pushMode, virObs } = self;
    let selGroup: any;
    if (obstId) {
        selGroup = obstGroup.children.find((item: any) => item.obstId === obstId);
    } else if (pushMode && virObs?.visible) {
        selGroup = virObs;
    } else {
        return {
            selGroup: null,
            obstMesh: null,
            obstFrame: null,
            inGroup: null,
        };
    }

    const inGroup = selGroup.children[0].children[0];

    return {
        selGroup,
        obstMesh: inGroup.children[0],
        obstFrame: inGroup.children[1],
        inGroup,
    }
}

function setObstDepth(obstMesh: any, obstFrame: { scale: { y: number; }; position: { y: number; }; }) {
    const { length, height, verFloor, rotX } = obstMesh;
    if (!rotX) return;
    const depth = verFloor ? Math.tan(-rotX) * length / 2 : 0;
    obstMesh.scale.y = obstFrame.scale.y = height + depth;
    obstMesh.position.y = obstFrame.position.y = (height / 2) - depth;
}

export function ChangeObstAngle(self: Viewer) {
    const { selGroup, obstMesh, inGroup, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    const { obstId, rotX } = obstMesh;
    const switchObstVer = document.getElementById('switchObstVer');
    const verFloor = !switchObstVer?.classList.contains('toggled');
    inGroup.rotation.x = verFloor ? 0 : rotX;
    obstMesh.verFloor = verFloor;
    setObstDepth(obstMesh, obstFrame);
    SetupSwitchObstRot(verFloor);

    self.refreshShade();
    self.addStep("obstAngle", obstId);
}

export function ChangeObstHeight(self: Viewer, value: number) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    obstMesh.height = value;
    setObstDepth(obstMesh, obstFrame);
}

export function ChangeObstWidth(self: Viewer, value: number, isUserUpdated = false) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    if (selGroup.isDefaultObstruction) {
        selGroup.userUpdatedDefaultDimensions = true;
    }
    obstMesh.width = value;
    obstMesh.radius = (obstMesh.width + obstMesh.length) / 2;
    obstMesh.diameter = obstMesh.radius;
    if (obstMesh.shapeNum === 1) obstMesh.scale.x = obstFrame.scale.x = value;
}

export function ChangeObstLength(self: Viewer, value: number, isUserUpdated = false) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    if (selGroup.isDefaultObstruction) {
        selGroup.userUpdatedDefaultDimensions = true;
    }
    obstMesh.length = value;
    obstMesh.radius = (obstMesh.width + value) / 2;
    obstMesh.diameter = obstMesh.radius;
    if (obstMesh.shapeNum === 1) {
        obstMesh.scale.z = obstFrame.scale.z = value;
        setObstDepth(obstMesh, obstFrame);
    }
}

export function ChangeObstSides(self: Viewer, value: number) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;

    obstMesh.sides = value;
    if (obstMesh.shapeNum === 2) {
        obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(2, value);
        obstMesh.scale.x = obstFrame.scale.x = obstMesh.radius;
        obstMesh.scale.z = obstFrame.scale.z = obstMesh.radius;
        //Update Old edge mesh
        const edgeMesh: any = obstMesh.children[0];
        if (edgeMesh) edgeMesh.geometry = new THREE.EdgesGeometry(obstMesh.geometry);

    }
}

export function ChangeObstDiameter(self: Viewer, value: number) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;

    obstMesh.diameter = value;
    obstMesh.width = value;
    obstMesh.length = value;
    obstMesh.radius = value;
    if (obstMesh.shapeNum === 2) {
        obstMesh.scale.x = obstFrame.scale.x = value;
        obstMesh.scale.z = obstFrame.scale.z = value;
        setObstDepth(obstMesh, obstFrame);
    }
}

export function ChangeObstRadius(self: Viewer, value: number, origin?: boolean) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;

    obstMesh.radius = value;
    obstMesh.width = value;
    obstMesh.length = value;
    obstMesh.diameter = value;
    if (obstMesh.shapeNum === 3 || origin) {
        obstMesh.scale.x = obstFrame.scale.x = value;
        obstMesh.scale.z = obstFrame.scale.z = value;
        setObstDepth(obstMesh, obstFrame);
    }
}

export function CreateObstBoxMesh(nodes: any[]) {
    const shape = new THREE.Shape();
    const center = { x: nodes[0].X, z: nodes[0].Z };
    const height = nodes[0].Y;

    for (let nodeIdx = 1; nodeIdx < nodes.length; nodeIdx++) {
        center.x += nodes[nodeIdx].X;
        center.z += nodes[nodeIdx].Z;
    }

    center.x /= nodes.length;
    center.z /= nodes.length;

    for (let nodeIdx = 0; nodeIdx < nodes.length; nodeIdx++) {
        const x = nodes[nodeIdx].X - center.x;
        const z = nodes[nodeIdx].Z - center.z;

        if (nodeIdx === 0) {
            shape.moveTo(x, z);
        } else {
            shape.lineTo(x, z);
        }
    }

    const mesh = new THREE.Mesh(
        new THREE.ExtrudeGeometry(shape, {
            depth: height,
            bevelEnabled: false,
        }),
        new THREE.MeshStandardMaterial({ color: 0xD3D3D3 }),
    );

    mesh.rotation.x = Math.PI / 2;
    mesh.position.set(center.x, height, center.z);
    return mesh;
}

export function ChangeObstShape(self: Viewer, value: number, nodes?: any[], normal_top?: { X: number; Y: number; Z: number }) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);



    if (!selGroup) {
        SetBtnObstActive(value);
        return;
    }

    switch (value) {
        case 1:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(value, obstMesh.sides);
            obstMesh.width = obstMesh.diameter;
            obstMesh.length = obstMesh.diameter;
            obstMesh.scale.x = obstFrame.scale.x = obstMesh.width;
            obstMesh.scale.z = obstFrame.scale.z = obstMesh.length;
            break;
        case 2:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(value, obstMesh.sides);
            obstMesh.width = obstMesh.diameter;
            obstMesh.length = obstMesh.diameter;
            obstMesh.scale.x = obstFrame.scale.x = obstMesh.width;
            obstMesh.scale.z = obstFrame.scale.z = obstMesh.length;
            break;
        case 3:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(value, obstMesh.sides);
            obstMesh.width = obstMesh.diameter;
            obstMesh.length = obstMesh.diameter;
            obstMesh.scale.x = obstFrame.scale.x = obstMesh.width;
            obstMesh.scale.z = obstFrame.scale.z = obstMesh.length;
            break;
        case 4:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(value, obstMesh.sides, nodes, normal_top);
            obstMesh.userData.normal_top = normal_top;
            break;
    }

    const edgeMesh: any = obstMesh.children[0];
    if (edgeMesh) edgeMesh.geometry = new THREE.EdgesGeometry(obstMesh.geometry);

    obstMesh.shapeNum = value;
    SetMeshOBB(obstMesh);
    SetBtnObstActive(value);
    SetupInputObstUI(obstMesh, selGroup.position);

    self.refreshShade();
}

export function ChangeObstDelete(self: Viewer) {
    const { selGroup } = GetObstSel(self);
    if (!selGroup) return;
    DeleteObst(selGroup, self);
}

export function DeleteObst(virObs: any, self: Viewer) {
    const inGroup = virObs.children[0].children[0], obstMesh = inGroup.children[0];
    useViewerStore.setState({ isGhostObstructionUpdated: !useViewerStore.getState().isGhostObstructionUpdated });
    if (obstMesh.delStatus) virObs.visible = true;
    obstMesh.delStatus = !obstMesh.delStatus;
    obstMesh.material.transparent = obstMesh.delStatus;
    obstMesh.material.opacity = obstMesh.delStatus ? delOpacity : 1;
    obstMesh.material.needsUpdate = true;
    const obstId = obstMesh.obstId;
    SetDelIconUI('Obst', obstMesh.delStatus);
    SetMaxFillForRoofs(self, obstMesh.roofId);
    self.reCalcShade([obstId], obstMesh.delStatus);

}

export function CheckDragableObst(e: MouseEvent, self: Viewer) {
    const { selTypeMeshArr, camera, cSize, obstGroup, obstId } = self;
    const downObst: any = GetClickObj(e, selTypeMeshArr, camera, cSize);
    if (downObst && downObst.object && downObst.object.obstId === obstId) {
        const dragObst: any = obstGroup.children.find((obst: any) => obst.obstId === obstId),
            { rotX, rotY, roofId } = dragObst.children[0].children[0].children[0];
        dragObst.oldInfo = { object: { rotX, rotY, roofFaceId: roofId }, point: { ...dragObst.position } };
        return dragObst;
    } else return null;
}

export function SetDragObst(e: MouseEvent, self: Viewer) {
    const { roofMeshArr, camera, cSize, dragObst, roofRotY, roofs, houseTransPos } = self;
    const interInfo = GetClickObj(e, [self.mapCustom, ...roofMeshArr], camera, cSize);
    if (!interInfo) return;
    let isObstructionOnRoof = false;
    if (interInfo.object.name.includes('roof')) {
        isObstructionOnRoof = true;
    }

    SetObstPosition(dragObst, interInfo, roofRotY, roofs[0].faces, houseTransPos, isObstructionOnRoof);
}

export function PutDragObst(self: Viewer) {
    const { obstGroup, dragObst, roofRotY, roofs, moduleMeshArr, houseTransPos } = self;
    const verPosArr = GetVerPosArr(dragObst);
    const overFlag = CheckOverObstArr(obstGroup, moduleMeshArr, dragObst.roofId, verPosArr, dragObst.obstId);
    if (overFlag) {
        SetObstPosition(dragObst, dragObst.oldInfo, roofRotY, roofs[0].faces, houseTransPos);
    } else {
        dragObst.children[0].children[0].children[0].verPosArr = verPosArr;
        const { obstId, oldInfo, position, rotX, rotY, roofId } = dragObst;
        dragObst.isObtsructionOnGround = position.y <= 0.2;
        const newInfo: any = { point: { ...position }, object: { rotX, rotY } }
        AddStep(self, 'obstMove', obstId, oldInfo, newInfo);
        self.refreshShade();
    }
    self.dragObst = null;
}

export function SetObstPosition(obstObj: any, interInfo: any, roofRotY: number, faces?: any, houseTransPos?: any, isObstructionOnRoof?: boolean) {
    const { point, object } = interInfo, { rotX, rotY, roofFaceId } = object;
    const obstMesh = obstObj.children[0].children[0].children[0];
    if (isObstructionOnRoof) {
        if (obstMesh.shapeNum === 4) { return }
        obstMesh.rotX = rotX || 0;
        obstMesh.rotY = rotY || 0;
        obstMesh.roofId = obstObj.roofId = roofFaceId;
        obstMesh.roofPos = { x: point.x - houseTransPos?.x, y: point.y, z: point.z - houseTransPos?.z };
        obstObj.rotation.y = roofRotY || 0;
        obstObj.children[0].rotation.y = rotY || 0;
    } else {
        obstMesh.rotX = rotX || 0; //Set obstruction parallel to terrain
    }
    SetOutsideObstPos(obstObj, point);
}

export function SetOutsideObstPos(obstObj: any, oriPos: { x: number, y: number, z: number }, faces?: any) {
    const inGroup = obstObj.children[0].children[0], obstMesh = inGroup.children[0], obstFrame = inGroup.children[1];
    const { width, length, height, verFloor, rotX } = obstMesh;
    // obstMesh.outside = CheckOutsideFaces(faces, oriPos, width, length);
    if (obstMesh.outside) {
        const top = oriPos.y + height;
        inGroup.rotation.x = 0;
        obstMesh.scale.y = obstFrame.scale.y = top;
        obstMesh.position.y = obstFrame.position.y = top / 2;
        obstObj.position.set(oriPos.x, 0, oriPos.z);
        obstMesh.worldPos = { x: oriPos.x, y: 0, z: oriPos.z };
        obstObj.isObtsructionOnGround = true;
    } else {
        if (obstMesh.shapeNum === 4) { return }
        const depth = obstMesh.roofId && verFloor ? Math.tan(-rotX) * length / 2 : 0;
        inGroup.rotation.x = verFloor ? 0 : rotX || 0;
        obstMesh.scale.y = obstFrame.scale.y = height + depth;
        obstMesh.position.y = obstFrame.position.y = (height / 2) - depth;
        obstObj.position.set(oriPos.x, oriPos.y, oriPos.z);
        obstMesh.worldPos = { x: oriPos.x, y: oriPos.y, z: oriPos.z };
    }
    obstObj.posY = obstObj.position.y;
}

export function HideObject(self: Viewer, type: string, value: boolean) {
    const isTreeLabelVisible = useViewerStore.getState().isTreeLabelVisible;
    const isTreeMultiplierLabelVisible = useViewerStore.getState().isTreeMultiplierLabelVisible;
    if (type === 'GhostObst') {
        var flagChangeObst = false;
        self.obstGroup.children.forEach(group => {
            if (!group.children[0] || !group.children[0].children[0]) return;
            const obstMesh: any = group.children[0].children[0].children[0];
            if (obstMesh.delStatus) {
                group.visible = !value;
                flagChangeObst = true;
            }
        });
    } else if (type.includes('AllObst')) {
        const obstIds: string[] = [];
        self.obstGroup.children.forEach(group => {
            group.visible = !value;
            if (type === 'WhatIfAllObst') {
                const obstMesh: any = group.children[0]?.children[0]?.children[0];
                if (!obstMesh) return;
                if (value) {
                    obstMesh.oldDelStatus = obstMesh.delStatus;
                    obstMesh.delStatus = true;
                    obstIds.push(obstMesh.obstId);
                } else {
                    obstMesh.delStatus = obstMesh.oldDelStatus;
                    if (!obstMesh.oldDelStatus) obstIds.push(obstMesh.obstId);
                }
            }
        });
        self.reCalcShade(obstIds, value);
    } else if (type === 'GhostTree') {
        var flagChangeTree = false;
        self.treeGroup.children.forEach((group: any) => {
            if (group.delStatus) {
                if (isTreeMultiplierLabelVisible) {
                    self.treeLabelGroup.children.forEach(labelGroup => {
                        if (labelGroup.userData.treeid === group.treeId) {
                            labelGroup.visible = !value;
                        }
                    })
                }
                if (isTreeLabelVisible) {
                    self.treeTagGroup.children.forEach(tagGroup => {
                        if (tagGroup.name === group.treeId) {
                            tagGroup.visible = !value;
                        }
                    })
                }
                group.visible = !value;
                flagChangeTree = true;
            }
        });
    } else if (type === 'SelTree') {
        var flagChangeTree = false;
        self.treeGroup.children.forEach((group: any) => {
            if (group.select) {
                if (isTreeMultiplierLabelVisible) {
                    self.treeLabelGroup.children.forEach(labelGroup => {
                        if (labelGroup.userData.treeid === group.treeId) {
                            labelGroup.visible = !value;
                        }
                    })
                }
                if (isTreeLabelVisible) {
                    self.treeTagGroup.children.forEach(tagGroup => {
                        if (tagGroup.name === group.treeId) {
                            tagGroup.visible = !value;
                        }
                    })
                }
                group.visible = !value;
                flagChangeTree = true;
            }
        });
    } else if (type.includes('AllTrees')) {
        const obstIds: string[] = [];
        self.treeGroup.children.forEach((group: any) => {
            group.visible = !value;
            if (type === 'WhatIfAllTrees') {
                if (value) {
                    group.oldDelStatus = group.delStatus;
                    group.delStatus = true;
                    obstIds.push(group.userData.treeId);
                } else {
                    group.delStatus = group.oldDelStatus;
                    if (!group.oldDelStatus) obstIds.push(group.userData.treeId);
                }
            }
        });
        self.reCalcShade(obstIds, value);
    } else if (type === 'ModuleTexture') {
        SwitchModuleIrrMat(self, value);
    } else if (type === 'ModulePathway') {
        self.flagModulePathway = value;
        self.roofMeshArr.forEach(roofMesh => {
            addModulesToRoofAutoFill(self, roofMesh,null,null,true);
        })
    }
}

export function SetObstVPos(obstGroup: THREE.Group) {
    obstGroup.children.forEach((child: any) => {
        child.vPos = new THREE.Box3().setFromObject(child);
    });
}

export function CheckDefaultObst(obstArr: any[]) {
    obstArr.forEach((obst) => {
        const obstMesh = obst.children[0].children[0].children[0],
            frameMesh = obst.children[0].children[0].children[1],
            { shapeNum, verPosArr, nodes, width, length } = obstMesh;

        // Only apply to shape type 1
        if (shapeNum !== 1) return;

        // Calculate distances between first node and each vertex
        let disMin = Infinity;
        verPosArr.forEach((verPos: { x: number; y: number; z: number; }) => {
            const disNode = GetDis(nodes[0], verPos);
            if (disNode < disMin) {
                disMin = disNode;
            }
        });

        // Add extra validation before swapping dimensions
        const shouldSwap = disMin > (width + length) / 10;

        if (shouldSwap) {
            // Check if swapping improves alignment
            const swappedWidth = length;
            const swappedLength = width;

            const alignmentBefore = Math.abs(width - length);
            const alignmentAfter = Math.abs(swappedWidth - swappedLength);

            if (alignmentAfter < alignmentBefore) {
                // Apply the swap only if it improves alignment
                obstMesh.width = swappedWidth;
                obstMesh.length = swappedLength;

                obstMesh.scale.x = frameMesh.scale.x = swappedWidth;
                obstMesh.scale.z = frameMesh.scale.z = swappedLength;

                obstMesh.verPosArr = GetVerPosArr(obst);
            }
        }
    });
}



export function AdjustObstOrientation(obst: any) {
    if (!obst.isDefaultObstruction) return;

    // see https://en.wikipedia.org/wiki/Curve_orientation
    const isClockwise = (points: THREE.Vector3[]) => {
        const a = points[0];
        const b = points[1];
        const c = points[2];

        return (b.x * c.z + a.x * b.z + a.z * c.x) - (a.z * b.x + b.z * c.x + a.x * c.z) > 0;
    };

    const obstMesh = obst.children[0].children[0].children[0];
    const obstFrame = obst.children[0].children[0].children[1];
    const nodes = obstMesh.nodes?.map((n: { X: number; Z: number }) => new THREE.Vector3(n.X, 0, n.Z));

    if (nodes?.length === 4) {
        if (isClockwise(nodes)) {
            nodes.reverse();
        }

        const p0 = nodes[0];
        const p1 = nodes[1];
        const p2 = nodes[2];
        const p3 = nodes[3];
        const pMid = p0.clone().add(p1).add(p2).add(p3).divideScalar(4);

        const verPosArr = obstMesh.verPosArr.map((v: { x: number; z: number }) => new THREE.Vector3(v.x, 0, v.z));

        if (isClockwise(verPosArr)) {
            verPosArr.reverse();
        }

        const q0 = verPosArr[0];
        const q1 = verPosArr[1];
        const q2 = verPosArr[2];
        const q3 = verPosArr[3];
        const qMid = q0.clone().add(q1).add(q2).add(q3).divideScalar(4);

        const toPolar = (origin: THREE.Vector3, point: THREE.Vector3) => {
            const dx = point.x - origin.x;
            const dz = point.z - origin.z;

            const atan = Math.atan2(dz, dx);

            return {
                r: Math.sqrt(dx * dx + dz * dz),
                theta: atan > 0 ? atan : atan + 2 * Math.PI,
            };
        };

        let u0, u1, v0, v1;

        if (p0.distanceTo(p1) < p1.distanceTo(p2)) {
            u0 = p1;
            u1 = p2;
        } else {
            u0 = p0;
            u1 = p1;
        }

        if (q0.distanceTo(q1) < q1.distanceTo(q2)) {
            v0 = q1;
            v1 = q2;
        } else {
            v0 = q0;
            v1 = q1;
        }

        const polarP = toPolar(pMid, u0.clone().add(u1).divideScalar(2));
        const polarV = toPolar(qMid, v0.clone().add(v1).divideScalar(2));
        var angle = polarV.theta - polarP.theta;


        const siteJSON = useViewerStore.getState().sitejson;

        const userUpdatedDefaultDimensions = obst.userUpdatedDefaultDimensions;


        let shouldRotate = false

        if (Math.abs(angle) % (Math.PI / 2) < 0.01) {
            shouldRotate = true;
        }

        if (siteJSON.hasOwnProperty('irradianceMonth') && userUpdatedDefaultDimensions && shouldRotate) {
            angle += Math.PI / 2;
            obst.rotation.y = angle;
        }
        else {
            obstMesh.rotation.y = angle;
            obstFrame.rotation.y = angle;

        }

        obstMesh.selfRot = angle;
        obstMesh.verPosArr = GetVerPosArr(obst);
    }
}

export function CheckModulesOverlay(self: Viewer) {
    const { meshArr, moduleMeshArr } = self;
    const obstructionMeshArr = meshArr.filter(mesh => mesh.meshType == 'obst');

    for (const obstruction of obstructionMeshArr) {
        UpdateMeshOBB(obstruction);

        for (const module of moduleMeshArr) {
            UpdateMeshOBB(module);

            const alignedModuleObb = module.userData.obb.clone();
            alignedModuleObb.center.y = obstruction.userData.obb.center.y;

            const intersects = obstruction.userData.obb.intersectsOBB(alignedModuleObb);

            if (intersects) {
                obstruction.material.transparent = true;
                obstruction.material.opacity = 0.5;
                obstruction.material.needsUpdate = true;
                break;
            } else {
                obstruction.material.transparent = false;
                obstruction.material.opacity = 1;
                obstruction.material.needsUpdate = true;
            }
        }
    }
}

export function SetObstProperty(self: Viewer, type: string, value: any) {
    switch (type) {
        case 'angle':
            return ChangeObstAngle(self);
        case 'height':
            return ChangeObstHeight(self, value);
        case 'length':
            return ChangeObstLength(self, value);
        case 'width':
            return ChangeObstWidth(self, value);
        case 'sides':
            return ChangeObstSides(self, value);
        case 'diameter':
            return ChangeObstDiameter(self, value);
        case 'radius':
            return ChangeObstRadius(self, value);
        case 'shape':
            return ChangeObstShape(self, value);
        case 'delete':
            return ChangeObstDelete(self);
    }
}

export function SetTreeProperty(self: Viewer, type: TreeProperty, value: any) {
    if (type === TreeProperty.posX || type === TreeProperty.posY) ChangeTreePos(self, type, value);
    else if (type === TreeProperty.delete) ToggleSelectedTreeDelete(self);
    else if (type === TreeProperty.shape) ChangeSelectedTreeShape(self, value);
    else ChangeTreeSize(self, type, value);
}